import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Star } from "lucide-react";
import { useReviews, http, errMsg } from "../lib/api";
import { Stars } from "./Stars";

const ReviewForm = ({ slug, onDone }) => {
  const [form, setForm] = useState({ name: "", city: "", rating: 0, comment: "" });
  const [busy, setBusy] = useState(false);
  const qc = useQueryClient();
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.rating) return toast.error("Pick a star rating first");
    setBusy(true);
    try {
      await http.post(`/products/${slug}/reviews`, form);
      toast.success("Thanks! Your review is live.");
      qc.invalidateQueries({ queryKey: ["reviews", slug] });
      qc.invalidateQueries({ queryKey: ["product", slug] });
      qc.invalidateQueries({ queryKey: ["products"] });
      onDone();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const field = "h-11 w-full rounded-md border border-input bg-background px-4 text-sm outline-none focus:border-brass";
  return (
    <form data-testid="review-form" onSubmit={submit} className="space-y-4 rounded-md border border-border bg-card p-6">
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((i) => (
          <button type="button" key={i} data-testid={`review-star-${i}`} onClick={() => setForm({ ...form, rating: i })} aria-label={`${i} stars`}>
            <Star className={`h-7 w-7 transition-transform hover:scale-110 ${i <= form.rating ? "fill-brass text-brass" : "text-brass/40"}`} />
          </button>
        ))}
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <input data-testid="review-name-input" required minLength={2} placeholder="Your name (e.g. Asha & Dev)" value={form.name} onChange={set("name")} className={field} />
        <input data-testid="review-city-input" placeholder="City" value={form.city} onChange={set("city")} className={field} />
      </div>
      <textarea data-testid="review-comment-input" required minLength={10} rows={4} placeholder="How was the build? How is it holding up?" value={form.comment} onChange={set("comment")} className={`${field} h-auto py-3`} />
      <button data-testid="review-submit-button" disabled={busy} className="btn-sheesham">{busy ? "Posting" : "Post review"}</button>
    </form>
  );
};

export const Reviews = ({ product }) => {
  const { data: reviews = [] } = useReviews(product.slug);
  const [writing, setWriting] = useState(false);
  return (
    <section data-testid="reviews-section" id="reviews" className="grid gap-10 lg:grid-cols-12">
      <div className="lg:col-span-4">
        <p className="eyebrow">Reviews</p>
        <h2 className="mt-3 text-3xl sm:text-4xl">From homes that built it</h2>
        <div className="mt-6 flex items-center gap-4">
          <span data-testid="review-average" className="font-display text-5xl">{product.rating || "–"}</span>
          <div>
            <Stars value={product.rating} size={18} />
            <p data-testid="review-count" className="mt-1 text-sm text-muted-foreground">{product.review_count} reviews</p>
          </div>
        </div>
        {!writing && <button data-testid="write-review-button" onClick={() => setWriting(true)} className="btn-outline mt-6">Write a review</button>}
      </div>
      <div className="space-y-6 lg:col-span-8">
        {writing && <ReviewForm slug={product.slug} onDone={() => setWriting(false)} />}
        {reviews.length === 0 && <p className="text-muted-foreground">No reviews yet. Be the first to tell others how the build went.</p>}
        <ul className="divide-y divide-border">
          {reviews.map((r) => (
            <li key={r.id} data-testid="review-item" className="py-6 first:pt-0">
              <div className="flex items-center justify-between gap-4">
                <Stars value={r.rating} />
                <span className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</span>
              </div>
              <p className="mt-3 leading-relaxed">{r.comment}</p>
              <p className="mt-2 text-sm font-medium text-muted-foreground">{r.name}{r.city && `, ${r.city}`}</p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};
